import { useEffect, useState, type MouseEvent } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PageHeader } from '@/components/PageHeader';
import { StackDetailForm } from '@/components/catalog/StackDetailForm';
import { api } from '@/lib/api';
import type { Catalog, CatalogStack, CreateCatalogStackRequest, UpdateCatalogStackRequest } from '@/types/catalog';

export function StackDetailPage() {
  const { catalogId, stackId } = useParams<{ catalogId: string; stackId: string }>();
  // Keyed by route: moving to another stack remounts the view, so its loading state and the form's
  // baseline start fresh without any state reset inside an effect.
  return <StackDetailView key={`${catalogId}:${stackId}`} catalogId={catalogId} stackId={stackId} />;
}

interface StackDetailViewProps {
  catalogId: string | undefined;
  stackId: string | undefined;
}

function StackDetailView({ catalogId, stackId }: StackDetailViewProps) {
  const navigate = useNavigate();

  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [stack, setStack] = useState<CatalogStack | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Set when an in-app link was clicked with unsaved edits; the confirm dialog decides whether to go.
  const [leaveTo, setLeaveTo] = useState<string | null>(null);

  const isNew = stackId === 'new';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const catalogData = await api.catalog.get(Number(catalogId));
        const stackData =
          !isNew && stackId ? await api.catalog.stacks.get(Number(catalogId), Number(stackId)) : null;
        if (cancelled) return;
        setCatalog(catalogData);
        setStack(stackData);
      } catch (err) {
        if (cancelled) return;
        toast.error(err instanceof Error ? err.message : 'Failed to load data');
        navigate(`/catalog/${catalogId}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [catalogId, stackId, isNew, navigate, reloadKey]);

  // Browser-level guard (reload, close tab, typed URL). Only the in-app links are guarded
  // below: the router blocker (useBlocker) needs a data router and this app uses BrowserRouter.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  // Rejects on failure so the form keeps the user's edits and shows the server message.
  const handleSave = async (data: CreateCatalogStackRequest | UpdateCatalogStackRequest) => {
    setSaving(true);
    try {
      if (isNew) {
        const newStack = await api.catalog.stacks.create(Number(catalogId), data as CreateCatalogStackRequest);
        toast.success('Stack created');
        navigate(`/catalog/${catalogId}/stacks/${newStack.id}`);
      } else {
        await api.catalog.stacks.update(Number(catalogId), Number(stackId), data as UpdateCatalogStackRequest);
        toast.success('Stack saved');
        setStack((prev) => ({ ...prev, ...data, id: Number(stackId) }) as CatalogStack);
      }
    } finally {
      setSaving(false);
    }
  };

  // Resolves true only when the delete succeeded; on failure the error stays visible in the dialog.
  const runDelete = async (): Promise<boolean> => {
    if (deleting) return false;
    setDeleting(true);
    setDeleteError(null);
    try {
      const result = await api.catalog.stacks.delete(Number(catalogId), Number(stackId));
      setDirty(false);
      if (result.warning) {
        toast.warning(result.warning);
      } else {
        toast.success('Stack deleted');
      }
      navigate(`/catalog/${catalogId}`);
      return true;
    } catch (err) {
      setDeleteError(err instanceof Error && err.message ? err.message : 'Failed to delete stack');
      return false;
    } finally {
      setDeleting(false);
    }
  };

  const handleRestore = async () => {
    setSaving(true);
    try {
      await api.catalog.stacks.restore(Number(catalogId), Number(stackId));
      toast.success('Stack restored');
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to restore stack');
    } finally {
      setSaving(false);
    }
  };

  // In-app navigation away from the page: ask first when there are unsaved edits.
  const guardNav = (to: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    if (!dirty) return;
    e.preventDefault();
    setLeaveTo(to);
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-label="Loading stack" />
      </div>
    );
  }

  if (!catalog) {
    return <div className="py-8 text-center text-muted-foreground">Catalog not found</div>;
  }

  const catalogPath = `/catalog/${catalogId}`;
  const pageTitle = isNew ? 'New stack' : `Edit stack: ${stack?.name || ''}`;

  return (
    <div className="space-y-6">
      <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-sm">
        <Link to="/catalog" onClick={guardNav('/catalog')} className="text-muted-foreground hover:text-foreground">
          Catalogs
        </Link>
        <span aria-hidden className="text-muted-foreground">/</span>
        <Link to={catalogPath} onClick={guardNav(catalogPath)} className="text-muted-foreground hover:text-foreground">
          {catalog.name || 'Catalog'}
        </Link>
        <span aria-hidden className="text-muted-foreground">/</span>
        <span className="text-muted-foreground">Stacks</span>
        <span aria-hidden className="text-muted-foreground">/</span>
        <span aria-current="page" className="font-medium">
          {isNew ? 'New' : stack?.name}
        </span>
      </nav>

      <PageHeader
        title={pageTitle}
        actions={
          !isNew && stack ? (
            <Badge variant={stack.is_active ? 'default' : 'destructive'}>{stack.is_active ? 'Active' : 'Deleted'}</Badge>
          ) : undefined
        }
      >
        <Link
          to={catalogPath}
          onClick={guardNav(catalogPath)}
          className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Back to {catalog.name || 'catalog'}
        </Link>
      </PageHeader>

      <StackDetailForm
        stack={stack}
        onSave={handleSave}
        onDelete={() => {
          setDeleteError(null);
          setDeleteOpen(true);
        }}
        onRestore={handleRestore}
        isNew={isNew}
        loading={saving || deleting}
        onDirtyChange={setDirty}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(next) => {
          if (!next && !deleting) {
            setDeleteOpen(false);
            setDeleteError(null);
          }
        }}
        title="Delete stack?"
        description={stack ? `Delete '${stack.name}'? It will be hidden from users and can be restored later.` : undefined}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        destructive
        loading={deleting}
        error={deleteError}
        onConfirm={async () => {
          if (await runDelete()) setDeleteOpen(false);
        }}
      />

      <ConfirmDialog
        open={leaveTo !== null}
        onOpenChange={(next) => {
          if (!next) setLeaveTo(null);
        }}
        title="Leave with unsaved changes?"
        description="Your edits to this stack have not been saved. Leaving now discards them."
        confirmLabel="Discard and leave"
        destructive
        onConfirm={() => {
          const target = leaveTo;
          setLeaveTo(null);
          setDirty(false);
          if (target) navigate(target);
        }}
      />
    </div>
  );
}
